import { useAuthState } from '@creditornot/wolt-auth/react';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import { Redirect, Route, Switch, useLocation, useParams } from 'react-router-dom';
import { useQueryParam, StringParam } from 'use-query-params';

import { getPhotoShootLink, getPhotoShoot } from 'apis/mips/api';
import { PhotoShootData } from 'apis/mips/types';
import { hasPermissions, usePermissions } from 'common/wolt-permissions';

import PhotoShootTemplate from './PhotoShootTemplate/PhotoShootTemplate';
import { transformPhotoShootToContextModel } from './utils';
import PhotoShootContext, { PhotoShootContextValue } from '../PhotoShootContext';
import { PhotoShootQueryKeys } from '../constants/queryKeys';
import { routes } from '../constants/routes';
import ApproveSuccessPage from '../pages/ApproveSuccessPage';
import LinkExpiredPage from '../pages/LinkExpiredPage';
import PhotoShootPage from '../pages/PhotoShootPage';
import SubmitSuccessPage from '../pages/SubmitSuccessPage';
import { PhotoShootModel } from '../types';

const PhotoShootRoutes = () => {
  const [tokenParam = null, setTokenParam] = useQueryParam('token', StringParam);
  const { venueId } = useParams<{ venueId: string }>();
  const permissions = usePermissions();
  const { pathname, search } = useLocation();

  const { user } = useAuthState();
  const hasEmployeeAccess = hasPermissions([{ resource: 'restaurant', operations: ['r', 'u'] }], permissions);
  const token = hasEmployeeAccess ? null : tokenParam;

  const { data: link, isFetching: isLinkLoading } = useQuery(
    PhotoShootQueryKeys.GET_PHOTO_SHOOT_LINK(venueId),
    () => getPhotoShootLink(venueId),
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!venueId && !!user,
    },
  );

  const { data, isLoading, isError } = useQuery<PhotoShootData, unknown, PhotoShootModel>(
    PhotoShootQueryKeys.GET_PHOTO_SHOOT(venueId, token),
    () => getPhotoShoot(venueId, token),
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!venueId && !isLinkLoading,
      select: transformPhotoShootToContextModel,
    },
  );

  const contextValue = useMemo<PhotoShootContextValue>(
    () => ({
      token,
      venueId,
      ...data,
      hasEmployeeAccess,
    }),
    [token, data, venueId, hasEmployeeAccess],
  );

  useEffect(() => {
    if (link?.url) {
      const token = new URL(link.url).searchParams.get('token');

      if (token !== tokenParam) {
        setTokenParam(token);
      }
    }
  }, [link, setTokenParam, tokenParam]);

  if (pathname !== routes.linkExpired(venueId) && isError) {
    return <Redirect to={`${routes.linkExpired(contextValue.venueId)}${search}`} />;
  }

  if (pathname === routes.linkExpired(venueId) && !isLoading && !isError) {
    return <Redirect to={`${routes.index(contextValue.venueId)}${search}`} />;
  }

  return (
    <PhotoShootTemplate
      venueName={contextValue.venueName}
      expiresIn={contextValue.expiresIn}
      status={contextValue.status}
      isLoading={isLoading}
    >
      <PhotoShootContext.Provider value={contextValue}>
        <Switch>
          <Route exact path={routes.index()} component={PhotoShootPage} />
          <Route exact path={routes.imagesSubmitted()} component={SubmitSuccessPage} />
          <Route exact path={routes.imagesApproved()} component={ApproveSuccessPage} />
          <Route exact path={routes.linkExpired()} component={LinkExpiredPage} />
        </Switch>
      </PhotoShootContext.Provider>
    </PhotoShootTemplate>
  );
};

export default PhotoShootRoutes;
